import { Box, Button, Group, ActionIcon, Tooltip, Text, Collapse, Badge } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faClone, faTrashCan, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@vision/ui/hooks/useAppDispatch';
import {
  removeRule,
  duplicateRule,
  updateConditionGroupsOperator,
  addConditionGroup,
} from '@vision/ui/store/states/survey-logic-builder-state';
import {
  SurveyLogicRule,
  SurveyLogicOperator,
  SurveyLogicScope,
  SurveyLogicElement,
} from '@vision/ui/interfaces/survey-logic-builder';
import { getElementLabel } from '@vision/ui/utils/business/survey-element.utils';
import { memo, useCallback, useMemo, useState, useRef } from 'react';
import { ActionBuilder } from '../ActionBuilder';
import { ConditionGroupBuilder } from '../ConditionGroupBuilder';
import { PopConfirm, TextWithEmoji } from '@vision/ui/components';
import { RuleConditionPreview } from './RuleConditionPreview';
import classes from './RuleAccordionItem.module.css';

const MAX_LABEL_LENGTH = 100;

type Props = {
  rule: SurveyLogicRule;
  index: number;
  isExpanded?: boolean;
  onToggle?: () => void;
  allElements: SurveyLogicElement[];
  activeLanguageCode: string;
};

function arePropsEqual(prevProps: Props, nextProps: Props): boolean {
  return (
    prevProps.rule === nextProps.rule &&
    prevProps.index === nextProps.index &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.allElements === nextProps.allElements &&
    prevProps.activeLanguageCode === nextProps.activeLanguageCode
  );
}

export const RuleAccordionItem = memo(function RuleAccordionItem({
  rule,
  index,
  isExpanded = false,
  onToggle,
  allElements,
  activeLanguageCode,
}: Props) {
  const { t } = useTranslation('page-survey-logic-builder');
  const { t: tCommon } = useTranslation('translation');
  const dispatch = useAppDispatch();

  const [deletePopConfirmOpened, setDeletePopConfirmOpened] = useState(false);
  const hasBeenExpandedRef = useRef(isExpanded);

  if (isExpanded && !hasBeenExpandedRef.current) {
    hasBeenExpandedRef.current = true;
  }

  const ruleDisplayInfo = useMemo(() => {
    const { action } = rule;
    const prefix = `${t('rulesList.rule')} ${index + 1}`;

    let questionLabels: string[] = [];

    if (action.scope === SurveyLogicScope.QUESTION && action.targetElementKeys?.length > 0) {
      questionLabels = action.targetElementKeys.map((key) => getElementLabel(allElements, key, activeLanguageCode));
    } else if (action.scope === SurveyLogicScope.OPTION && action.targetOptionKeys?.length > 0) {
      const uniqueQuestionKeys = [...new Set(action.targetOptionKeys.map((target) => target.questionKey || ''))];
      questionLabels = uniqueQuestionKeys
        .filter((key) => key)
        .map((key) => getElementLabel(allElements, key, activeLanguageCode));
    } else if (action.scope === SurveyLogicScope.EVENT_DISPATCH && action.targetEvents?.length > 0) {
      return { prefix, label: `[${action.targetEvents[0]}]`, fullLabel: '' };
    }

    if (questionLabels.length === 0) {
      return { prefix, label: '', fullLabel: '' };
    }

    const fullLabel = questionLabels.map((l) => `[${l}]`).join(' ');
    const truncatedLabel =
      fullLabel.length > MAX_LABEL_LENGTH ? `${fullLabel.substring(0, MAX_LABEL_LENGTH)}...` : fullLabel;

    return {
      prefix,
      label: truncatedLabel,
      fullLabel: fullLabel.length > MAX_LABEL_LENGTH ? fullLabel : '',
    };
  }, [rule, index, allElements, activeLanguageCode, t]);

  const conditionGroupsOperator = rule.conditionGroupsOperator || 'and';

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(duplicateRule(rule.id));
    },
    [dispatch, rule.id],
  );

  const handleDelete = useCallback(() => {
    dispatch(removeRule(rule.id));
    setDeletePopConfirmOpened(false);
  }, [dispatch, rule.id]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletePopConfirmOpened(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeletePopConfirmOpened(false);
  }, []);

  const handleConditionGroupsOperatorToggle = useCallback(() => {
    const currentOperator = rule.conditionGroupsOperator || 'and';
    const newOperator: SurveyLogicOperator = currentOperator === 'and' ? 'or' : 'and';
    dispatch(
      updateConditionGroupsOperator({
        ruleId: rule.id,
        operator: newOperator,
      }),
    );
  }, [dispatch, rule.id, rule.conditionGroupsOperator]);

  const handleAddConditionGroup = useCallback(() => {
    dispatch(addConditionGroup(rule.id));
  }, [dispatch, rule.id]);

  return (
    <Box className={classes.container} data-testid={`section-rule-accordion-${index}`}>
      <Box
        className={`${classes.header} ${isExpanded ? classes.headerExpanded : ''}`}
        onClick={onToggle}
        data-testid={`button-toggle-rule-${index}`}
      >
        <Group gap="xs" wrap="nowrap" className={classes.headerContent}>
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className={classes.chevron} />
          <Text className={classes.rulePrefix}>{ruleDisplayInfo.prefix}</Text>
          {ruleDisplayInfo.label &&
            (ruleDisplayInfo.fullLabel ? (
              <Tooltip label={ruleDisplayInfo.fullLabel} multiline={true} maw={400}>
                <Box>
                  <TextWithEmoji text={ruleDisplayInfo.label} className={classes.ruleLabel} />
                </Box>
              </Tooltip>
            ) : (
              <TextWithEmoji text={ruleDisplayInfo.label} className={classes.ruleLabel} />
            ))}

          <RuleConditionPreview
            rule={rule}
            ruleIndex={index}
            elements={allElements}
            activeLanguageCode={activeLanguageCode}
          />
        </Group>

        <Group gap="xs" wrap="nowrap" className={classes.headerActions} onClick={(e) => e.stopPropagation()}>
          <Tooltip label={tCommon('duplicate')}>
            <ActionIcon
              className={classes.cloneButton}
              onClick={handleDuplicate}
              data-testid={`button-duplicate-rule-${index}`}
            >
              <FontAwesomeIcon icon={faClone} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={tCommon('deleteAll')}>
            <PopConfirm
              title={t('deleteConfirm.title')}
              description={t('deleteConfirm.message')}
              btnConfirmLabel={tCommon('delete')}
              btnCancelLabel={tCommon('cancel')}
              onConfirm={handleDelete}
              onCancel={handleDeleteCancel}
              opened={deletePopConfirmOpened}
              autoToggle={false}
              position="bottom"
              confirmBtnProps={{ color: 'red', 'data-testid': 'button-confirm-delete' }}
            >
              <ActionIcon
                className={classes.deleteRuleButton}
                onClick={handleDeleteClick}
                data-testid={`button-delete-rule-${index}`}
              >
                <FontAwesomeIcon icon={faTrashCan} />
              </ActionIcon>
            </PopConfirm>
          </Tooltip>
        </Group>
      </Box>

      {hasBeenExpandedRef.current && (
        <Collapse in={isExpanded}>
          <Box className={classes.content}>
            <Box className={classes.conditionGroupsSection}>
              {(rule.conditionGroups || []).map((group, groupIndex) => (
                <Box key={group.id}>
                  <ConditionGroupBuilder
                    ruleId={rule.id}
                    group={group}
                    groupIndex={groupIndex}
                    elements={allElements}
                    activeLanguageCode={activeLanguageCode}
                    canDelete={(rule.conditionGroups || []).length > 1}
                  />

                  {groupIndex < (rule.conditionGroups || []).length - 1 && (
                    <Box className={classes.groupOperatorContainer}>
                      <Box className={classes.groupOperatorLine} />
                      <Box className={classes.groupOperatorToggle}>
                        <Text
                          className={`${classes.groupOperatorOption} ${conditionGroupsOperator === 'and' ? classes.groupOperatorActive : ''}`}
                          onClick={handleConditionGroupsOperatorToggle}
                        >
                          {t('operator.and')}
                        </Text>
                        <Text
                          className={`${classes.groupOperatorOption} ${conditionGroupsOperator === 'or' ? classes.groupOperatorActive : ''}`}
                          onClick={handleConditionGroupsOperatorToggle}
                        >
                          {t('operator.or')}
                        </Text>
                      </Box>
                      <Box className={classes.groupOperatorLine} />
                    </Box>
                  )}
                </Box>
              ))}

              <Button
                variant="subtle"
                size="sm"
                leftSection={<FontAwesomeIcon icon={faCirclePlus} className={classes.addConditionGroupIcon} />}
                onClick={handleAddConditionGroup}
                className={classes.addConditionGroupButton}
                data-testid={`button-add-condition-group-${index}`}
              >
                {t('ruleEditor.addConditionGroup')}
              </Button>
            </Box>

            <ActionBuilder
              ruleId={rule.id}
              action={rule.action}
              elements={allElements}
              activeLanguageCode={activeLanguageCode}
            />

            <Box className={classes.ruleNumberBadgeBox}>
              <Badge className={classes.ruleNumberBadge} color="blue" size="lg" radius="xl" fw={400} onClick={onToggle}>
                {t('rulesList.rule')} {index + 1}
                <FontAwesomeIcon icon={faChevronUp} className={classes.badgeChevron} />
              </Badge>
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}, arePropsEqual);
